import React, { useState } from "react";
import { FaExternalLinkAlt, FaFileAlt, FaImage, FaKey, FaPen, FaTrashAlt, FaVideo } from "react-icons/fa";
import { ErrorBox, Field, Options, UserOptions } from "./common";
import { errorMessage, fmtDate, nullIfBlank, numOrNull } from "../dmApi";

const EMPTY = { Content_Type: "Poster", Title: "", Body: "", Media_URL: "", Tool_Used: "", Prepared_By: "" };
const TOOLS = ["Canva", "Pollygen", "ChatGPT", "Gemini", "Photoshop", "CapCut", "Other"];

const ACTION_LABELS = {
  "Internal Review": "Send for internal review",
  "Client Review": "Send to client",
  Approved: "Approve",
  Rejected: "Reject",
  Draft: "Back to draft",
};

const statusKey = (status) => String(status || "").toLowerCase().replace(/\s+/g, "-");

function typeIcon(type) {
  const t = String(type || "").toLowerCase();
  if (/video|reel|short/.test(t)) return FaVideo;
  if (/poster|image|banner|carousel|creative/.test(t)) return FaImage;
  if (/keyword/.test(t)) return FaKey;
  return FaFileAlt;
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join("") : "?";
}

function ContentCard({ item, actions, onMove, onEdit, onDelete }) {
  const TypeIcon = typeIcon(item.Content_Type);
  const reviewed = [
    item.Reviewed_By_Name ? `Reviewed by ${item.Reviewed_By_Name}` : null,
    item.Approved_On ? `Approved ${fmtDate(item.Approved_On)}` : null,
  ].filter(Boolean);

  return (
    <article className="dmp-citem">
      <div className="dmp-citem__top">
        <span className="dmp-citem__type">
          <TypeIcon aria-hidden="true" />
          {item.Content_Type}
        </span>
        {item.Tool_Used ? <span className="dmp-citem__tool">{item.Tool_Used}</span> : null}
        <div className="dmp-citem__icons">
          {item.Media_URL ? (
            <a
              href={item.Media_URL}
              target="_blank"
              rel="noreferrer"
              className="dmp-citem__icon"
              title="View media"
              aria-label="View media"
            >
              <FaExternalLinkAlt />
            </a>
          ) : null}
          <button type="button" className="dmp-citem__icon" title="Edit" aria-label="Edit" onClick={onEdit}>
            <FaPen />
          </button>
          <button
            type="button"
            className="dmp-citem__icon dmp-citem__icon--danger"
            title="Delete"
            aria-label="Delete"
            onClick={onDelete}
          >
            <FaTrashAlt />
          </button>
        </div>
      </div>

      <div className="dmp-citem__title">{item.Title}</div>
      {item.Body ? <p className="dmp-citem__body">{item.Body}</p> : null}
      {item.Review_Notes ? <div className="dmp-citem__note">{item.Review_Notes}</div> : null}

      {item.Prepared_By_Name || reviewed.length ? (
        <div className="dmp-citem__foot">
          {item.Prepared_By_Name ? (
            <span className="dmp-citem__by">
              <span className="dmp-citem__avatar" aria-hidden="true">
                {initials(item.Prepared_By_Name)}
              </span>
              {item.Prepared_By_Name}
            </span>
          ) : null}
          {reviewed.length ? <span className="dmp-citem__reviewed">{reviewed.join(" · ")}</span> : null}
        </div>
      ) : null}

      {actions.length ? (
        <div className="dmp-citem__actions">
          {actions.map((target) => (
            <button
              key={target}
              type="button"
              className={`dmp-citem__action dmp-citem__action--${statusKey(target)}`}
              onClick={() => onMove(target)}
            >
              {ACTION_LABELS[target] || target}
            </button>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function ContentForm({ meta, initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ""])),
    Prepared_By: initial?.Prepared_By ? String(initial.Prepared_By) : "",
  }));
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      Content_Type: form.Content_Type,
      Title: form.Title.trim(),
      Body: nullIfBlank(form.Body),
      Media_URL: nullIfBlank(form.Media_URL.trim()),
      Tool_Used: nullIfBlank(form.Tool_Used),
      ...(form.Prepared_By ? { Prepared_By: numOrNull(form.Prepared_By) } : {}),
    });
  };

  return (
    <form onSubmit={submit} className="dmp-card">
      <div className="dmp-form-grid">
        <Field label="Type">
          <select value={form.Content_Type} onChange={set("Content_Type")}>
            <Options values={meta?.content_types} />
          </select>
        </Field>
        <Field label="Title *">
          <input required value={form.Title} onChange={set("Title")} placeholder="eastern-pride-poster-1" />
        </Field>
        <Field label="Tool used">
          <input list="dmp-tools" value={form.Tool_Used} onChange={set("Tool_Used")} />
          <datalist id="dmp-tools">
            {TOOLS.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </Field>
        <Field label="Prepared by">
          <select value={form.Prepared_By} onChange={set("Prepared_By")}>
            <UserOptions users={meta?.users} blank="Me" />
          </select>
        </Field>
        <Field label="Media URL (image / video / drive link)" full>
          <input value={form.Media_URL} onChange={set("Media_URL")} placeholder="https://…" />
        </Field>
        <Field label={form.Content_Type === "Keywords" ? "Keywords" : "Text / caption"} full>
          <textarea rows={4} value={form.Body} onChange={set("Body")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save content"}
        </button>
      </div>
    </form>
  );
}

/** Content pipeline: Draft → Internal Review → Client Review → Approved (or Rejected). */
function ContentBoard({ api, meta, projectId, content, onChange }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const statuses = meta?.review_statuses || [];
  const transitions = meta?.review_transitions || {};

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      await onChange();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    }
  };

  const save = async (body) => {
    setSaving(true);
    const ok = await run(() =>
      editing === "new" ? api.createContent(projectId, body) : api.updateContent(editing.Content_Id, body)
    );
    setSaving(false);
    if (ok) setEditing(null);
  };

  const move = (item, target) => {
    let notes;
    if (target === "Rejected") {
      notes = window.prompt("Why is this content rejected?");
      if (!notes) return;
    }
    run(() => api.reviewContent(item.Content_Id, { Review_Status: target, Review_Notes: notes }));
  };

  const remove = (item) => {
    if (window.confirm(`Delete "${item.Title}"?`)) run(() => api.deleteContent(item.Content_Id));
  };

  return (
    <>
      <div className="dmp-header dmp-header--section" style={{ marginBottom: 10 }}>
        <button type="button" className="dmp-btn" onClick={() => setEditing("new")}>
          Add content
        </button>
      </div>
      <ErrorBox message={error} />
      {editing ? (
        <ContentForm
          key={editing === "new" ? "new" : editing.Content_Id}
          meta={meta}
          initial={editing === "new" ? null : editing}
          saving={saving}
          onSubmit={save}
          onCancel={() => setEditing(null)}
        />
      ) : null}
      <div className="dmp-board">
        {statuses.map((status) => {
          const items = content.filter((c) => c.Review_Status === status);
          return (
            <div
              key={status}
              className={`dmp-board__col dmp-board__col--${statusKey(status)}${items.length ? "" : " is-empty"}`}
            >
              <div className="dmp-board__head">
                <span className="dmp-board__name">
                  <span className="dmp-board__dot" aria-hidden="true" />
                  {status}
                </span>
                <span className="dmp-board__count">{items.length}</span>
              </div>
              {items.map((item) => (
                <ContentCard
                  key={item.Content_Id}
                  item={item}
                  actions={transitions[status] || []}
                  onMove={(target) => move(item, target)}
                  onEdit={() => setEditing(item)}
                  onDelete={() => remove(item)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default ContentBoard;
